// wallpaperLibrary/wallpaperGenerators.js
//
// Stocked SVG WALLPAPER library (deck concept 01-wallpaper-svg-library:
// "Preset wallpapers" — every tile is generated art, no upload needed).
//
// Distinct from ./backgroundPatterns (small TILEABLE motifs). Here each generator
// paints a full 120x120 SCENE (layered mountains + sun, wave bands, concentric
// rings, curve-split gradients, triangle peaks) from a palette. Output is raw SVG
// markup; ./toDataUri turns it into a `data:image/svg+xml,...` URL that rides the
// EXISTING `bgWallpaper` image slot (currentBg === 2) — no new redux field, no
// contract change, no network, no raster: GPU-composited background images.
//
// Every generator is a PURE function: (palette, id) -> SVG string. Palettes are
// internal constants (not user input), but every colour is still passed through
// the safe-colour allow-list before interpolation — defence in depth, mirroring
// backgroundPatterns.

// ---- colour safety (mirrors backgroundPatterns) ----------------------------

// Accept #hex (3/4/6/8) and rgb()/rgba()/hsl()/hsla() built only from digits, %,
// ., commas, spaces, slashes. Reject anything carrying CSS/markup breakout chars.
const SAFE_COLOR_RE =
  /^(?:#[0-9a-fA-F]{3,8}|(?:rgb|rgba|hsl|hsla)\(\s*[0-9.,%\s/]+\))$/;

export const isSafeColor = (c) =>
  typeof c === "string" && SAFE_COLOR_RE.test(c.trim());

export const safeColor = (c, fallback) =>
  isSafeColor(c) ? c.trim() : fallback;

// Brand navy fallback if a palette entry is ever malformed.
const FALLBACK = "#123051";
const sc = (c) => safeColor(c, FALLBACK);

const VB = 120; // square scene viewBox (matches wheel wallpaper aspect)

const svgOpen = (id) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${VB}" height="${VB}" ` +
  `viewBox="0 0 ${VB} ${VB}" preserveAspectRatio="xMidYMid slice" ` +
  `data-iyuga-wallpaper="${id}">`;

const skyGradient = (id, top, bottom) =>
  `<defs><linearGradient id="sky-${id}" x1="0" y1="0" x2="0" y2="1">` +
  `<stop offset="0" stop-color="${sc(top)}"/>` +
  `<stop offset="1" stop-color="${sc(bottom)}"/>` +
  `</linearGradient></defs>`;

// ---- palettes --------------------------------------------------------------
// Each palette: sky[topLight, bottomTint], layers[back(light) -> front(dark)],
// accent (sun/highlight light tone). Palette #1 is the brand violet (#9B5AF8).

export const PALETTES = [
  {
    id: "violet",
    name: "Violet",
    sky: ["#EDE4FF", "#C9B2F5"],
    layers: ["#C7A8F0", "#A87BE8", "#7E4FD6"],
    accent: "#F3ECFF",
  },
  {
    id: "ocean",
    name: "Ocean",
    sky: ["#E2F1FB", "#A9D4EE"],
    layers: ["#8FC3E6", "#4E9FD4", "#2C6FA6"],
    accent: "#FBFEFF",
  },
  {
    id: "peach",
    name: "Peach",
    sky: ["#FDEDE4", "#F8C9B3"],
    layers: ["#F6B79E", "#EE8E6E", "#D65F3E"],
    accent: "#FFF6F0",
  },
  {
    id: "forest",
    name: "Forest",
    sky: ["#E7F3E9", "#B8DCC0"],
    layers: ["#96C7A2", "#5DA772", "#2F7D4A"],
    accent: "#F4FBF5",
  },
  {
    id: "blush",
    name: "Blush",
    sky: ["#FDE7F0", "#F6BED6"],
    layers: ["#F1A7C7", "#E578A6", "#C64F82"],
    accent: "#FFF2F8",
  },
  {
    id: "amber",
    name: "Amber",
    sky: ["#FEF3E2", "#FBD9A6"],
    layers: ["#F7C877", "#EFA23F", "#D67A18"],
    accent: "#FFFAF0",
  },
  {
    id: "slate",
    name: "Slate",
    sky: ["#EEF1F5", "#C7D0DB"],
    layers: ["#AAB6C4", "#7D8C9E", "#526073"],
    accent: "#F8FAFC",
  },
  {
    id: "teal",
    name: "Teal",
    sky: ["#E2F5F1", "#A9E4D8"],
    layers: ["#86D6C6", "#46B7A2", "#2A8877"],
    accent: "#F4FDFB",
  },
];

// ---- motif generators ------------------------------------------------------
// Each: (palette, id) -> SVG markup string.

const layeredPeaks = (pal, id) => {
  const [l0, l1, l2] = pal.layers;
  return (
    svgOpen(id) +
    skyGradient(id, pal.sky[0], pal.sky[1]) +
    `<rect width="${VB}" height="${VB}" fill="url(#sky-${id})"/>` +
    // sun, tucked behind the back ridge
    `<circle cx="86" cy="40" r="15" fill="${sc(pal.accent)}" opacity="0.9"/>` +
    // back ridge (lightest)
    `<path d="M0 120 L0 78 L22 60 L44 80 L70 52 L96 74 L120 62 L120 120 Z" fill="${sc(l0)}"/>` +
    // mid ridge
    `<path d="M0 120 L0 92 L30 70 L55 90 L82 66 L110 88 L120 80 L120 120 Z" fill="${sc(l1)}"/>` +
    // front ridge (darkest)
    `<path d="M0 120 L0 104 L26 88 L52 104 L84 86 L120 106 L120 120 Z" fill="${sc(l2)}"/>` +
    `</svg>`
  );
};

const waveBands = (pal, id) => {
  const [l0, l1, l2] = pal.layers;
  return (
    svgOpen(id) +
    skyGradient(id, pal.sky[0], pal.sky[1]) +
    `<rect width="${VB}" height="${VB}" fill="url(#sky-${id})"/>` +
    // faint highlight specks near the top
    `<circle cx="24" cy="20" r="1.6" fill="${sc(pal.accent)}"/>` +
    `<circle cx="52" cy="12" r="1.2" fill="${sc(pal.accent)}"/>` +
    `<circle cx="90" cy="24" r="1.8" fill="${sc(pal.accent)}"/>` +
    `<circle cx="104" cy="14" r="1.2" fill="${sc(pal.accent)}"/>` +
    // three stacked wave bands (back light -> front dark)
    `<path d="M0 66 Q30 54 60 66 T120 66 L120 120 L0 120 Z" fill="${sc(l0)}"/>` +
    `<path d="M0 84 Q30 74 60 84 T120 84 L120 120 L0 120 Z" fill="${sc(l1)}"/>` +
    `<path d="M0 102 Q30 94 60 102 T120 102 L120 120 L0 120 Z" fill="${sc(l2)}"/>` +
    `</svg>`
  );
};

const concentricRings = (pal, id) => {
  const [l0, l1, l2] = pal.layers;
  const ringColors = [l2, l1, l0, l1, l2, l0];
  const rings = ringColors
    .map(
      (c, i) =>
        `<circle cx="78" cy="84" r="${12 + i * 12}" fill="none" ` +
        `stroke="${sc(c)}" stroke-width="4"/>`
    )
    .join("");
  return (
    svgOpen(id) +
    `<rect width="${VB}" height="${VB}" fill="${sc(pal.sky[0])}"/>` +
    rings +
    `<circle cx="78" cy="84" r="5" fill="${sc(l2)}"/>` +
    `</svg>`
  );
};

const curveSplit = (pal, id) => {
  const [l0, l1, l2] = pal.layers;
  return (
    svgOpen(id) +
    `<rect width="${VB}" height="${VB}" fill="${sc(pal.sky[0])}"/>` +
    `<path d="M0 40 C40 25 80 55 120 38 L120 120 L0 120 Z" fill="${sc(l0)}"/>` +
    `<path d="M0 66 C40 52 80 82 120 64 L120 120 L0 120 Z" fill="${sc(l1)}"/>` +
    `<path d="M0 92 C40 80 80 106 120 90 L120 120 L0 120 Z" fill="${sc(l2)}"/>` +
    `</svg>`
  );
};

const trianglePeak = (pal, id) => {
  const [l0, l1, l2] = pal.layers;
  return (
    svgOpen(id) +
    `<rect width="${VB}" height="${VB}" fill="${sc(pal.sky[1])}"/>` +
    `<circle cx="26" cy="30" r="11" fill="${sc(pal.accent)}" opacity="0.9"/>` +
    // back centre peak (lightest)
    `<path d="M60 40 L118 120 L2 120 Z" fill="${sc(l0)}"/>` +
    // left peak (mid)
    `<path d="M26 66 L70 120 L-6 120 Z" fill="${sc(l1)}"/>` +
    // right peak (darkest)
    `<path d="M96 58 L150 120 L48 120 Z" fill="${sc(l2)}"/>` +
    `</svg>`
  );
};

// ---- registry --------------------------------------------------------------

export const MOTIFS = [
  { id: "peaks", label: "Layered Peaks", generator: layeredPeaks },
  { id: "waves", label: "Wave Bands", generator: waveBands },
  { id: "rings", label: "Concentric Rings", generator: concentricRings },
  { id: "curves", label: "Curve Split", generator: curveSplit },
  { id: "triangles", label: "Triangle Peaks", generator: trianglePeak },
];

// Stable, ordered preset list: motif-major so a row groups a motif across colours
// exactly as the deck's grid reads. id === `<paletteId>-<motifId>` (stored in the
// data-URI marker so a saved wallpaper is self-describing / recoverable).
export const WALLPAPER_PRESETS = MOTIFS.flatMap((motif) =>
  PALETTES.map((pal) => ({
    id: `${pal.id}-${motif.id}`,
    label: `${pal.name} ${motif.label}`,
    paletteId: pal.id,
    motifId: motif.id,
    generator: () => motif.generator(pal, `${pal.id}-${motif.id}`),
  }))
);

export const WALLPAPER_PRESET_BY_ID = WALLPAPER_PRESETS.reduce((acc, p) => {
  acc[p.id] = p;
  return acc;
}, {});

// Generate the raw SVG markup for a preset id. Returns "" for unknown ids
// (defensive: never throw into a render path).
export const generateWallpaperSvg = (id) => {
  const entry = WALLPAPER_PRESET_BY_ID[id];
  if (!entry) return "";
  return entry.generator();
};
